import * as mediaQuery from "components/MediaQuery";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ProfileAccountInfo,
  ProfileAccountInfoFromServer,
} from "api/interfaces/accountInterface.interface";
import {
  buildMenuListsForAccount,
  checkRoleMenu,
  buildVerify2faNavigatePayload,
  getSwitchAccountRoleId,
  mergeSwitchAccountQueryString,
  resolveSwitchAccountNavigatePath,
  shouldRequire2faForSwitchAccount,
  adjustedStartPage,
  hasPermissionForPath,
  getPermittedPath as resolvePermittedPath,
  normalizeMenuPath,
  buildPermissionLocationKey,
  createPermissionRedirectGuard,
} from "utils/MenuUtil";
import { debounce } from "lodash";
import {
  MenuNavigateQueryString,
  RoutesProps,
  SwitchAccountNavigateOptions,
} from "api/interfaces/menuInterface.interface";
import {
  DefaultQueryParam,
  LEVEL_TYPE,
} from "api/interfaces/commonInterface.interface";
import {
  getExpiredFromTrialAndChannel,
  getSwitchAccountInfo,
  checkIsSuspended,
  checkIsSubCP,
} from "utils/AccountUtil";
import { getStartPageUrl } from "utils/functions";
import {
  changeToAccountFormat,
  setAccountSetting,
} from "redux/reducers/account/accountSlice";
import { useAuth } from "components/Auth";
import { useCallback, useEffect, useMemo, useRef } from "react";
import useApiError from "./useApiError";
import { getCookie } from "cookies/cookie";
import { ACCESS_TOKEN } from "api/axiosInstance";
import { setIsAuth } from "redux/reducers/auth/isAuth";
import { SUSPENSION_STATUS } from "redux/stomp/stompSubscriptionSlice";
import { useFlag } from "@unleash/proxy-client-react";
import { getRoleId } from "utils/lastPathUtil";
import { getUseRoleType, setAxiosHeaders } from "utils/AuthUtil";

export interface UseMenuProps {}

export function useMenu(props: UseMenuProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { handleErrorNotify, lang, notifyError } = useApiError();
  const auth = useAuth();
  const { isAuthenticated } = useAppSelector((state) => state.isAuth);
  const location = useLocation();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const isFirmwareUpgradeEnable = useFlag("feature_manage_firmwareUpgrade");
  const isCovaConfigEnable = useFlag("feature_report_inventory_cova_config");
  const isStagingRecorderEnable = useFlag("feature_maintenance_staging");
  const isPlanHide = useFlag("feature_plan_hide");
  const lastNeedUpdateCurrentAccount = useRef<boolean>(false);
  const permissionCheckGenerationRef = useRef(0);
  const permissionRedirectGuardRef = useRef(createPermissionRedirectGuard());
  const locationRef = useRef(location);
  const selectedAccountRef = useRef(selectedAccount);
  locationRef.current = location;
  selectedAccountRef.current = selectedAccount;

  useEffect(() => {
    const guard = permissionRedirectGuardRef.current;
    return () => {
      permissionCheckGenerationRef.current += 1;
      guard.reset();
    };
  }, []);

  const updateCurrentAccount = useCallback(
    async (onSuccess?: () => void) => {
      if (
        !auth.user.userId ||
        !selectedAccount.accountId ||
        lastNeedUpdateCurrentAccount.current ||
        !getCookie(ACCESS_TOKEN)
      ) {
        return;
      }
      lastNeedUpdateCurrentAccount.current = true;
      const roleId = getRoleId();
      getSwitchAccountInfo(selectedAccount.accountId, roleId)
        .then((accountInfo) => {
          if (!!accountInfo) {
            dispatch(setAccountSetting(accountInfo));
            onSuccess?.();
          }
        })
        .finally(() => {
          lastNeedUpdateCurrentAccount.current = false;
        });
    },
    [selectedAccount.accountId, auth.user.userId]
  );

  const debouncedNavigate = debounce(
    ({
      to,
      replace,
      state,
      queryString,
      onComplete,
    }: {
      to: string;
      replace?: boolean;
      state?: any;
      queryString?: MenuNavigateQueryString;
      onComplete?: () => void;
    }) => {
      navigate(buildMenuRouteTo({ to, state, queryString }), {
        replace,
        state,
      });

      if (onComplete) {
        onComplete();
      }
    },
    300
  );
  const menuNavigation = useCallback(
    ({
      to,
      replace,
      state,
      queryString,
      onComplete,
    }: {
      to: string;
      replace?: boolean;
      state?: any;
      queryString?: MenuNavigateQueryString;
      onComplete?: () => void;
    }) => {
      const realQuery: MenuNavigateQueryString = { ...(queryString ?? {}) };
      if (!realQuery.r) {
        realQuery.r = getRoleId({
          accountId: selectedAccount.accountId,
        });
      }
      debouncedNavigate({
        to,
        replace,
        state,
        queryString,
        onComplete,
      });
    },
    [debouncedNavigate]
  );

  const queryNavigate = useCallback(
    ({ to, queryInfo }: { to: string; queryInfo: DefaultQueryParam }) => {
      let realQuery = Object.entries(queryInfo)
        .filter((data) => data[1] !== null && data[1] !== undefined)
        .map((e) => {
          if (e[1] !== undefined && e[1] !== null) {
            return e[0].concat("=").concat(encodeURIComponent(e[1]));
          } else {
            return "";
          }
        })
        .join("&");
      if (!queryInfo.r) {
        realQuery = `r=${getRoleId({
          accountId: selectedAccount.accountId,
        })}&${realQuery}`;
      }
      //navigate(`${to}?${realQuery}`);
      realQuery = `account=${selectedAccount.accountId}&${realQuery}`;

      navigate({
        pathname: to,
        search: `?${realQuery}`,
      });
    },
    [selectedAccount, navigate]
  );

  const checkRolePermission = useCallback(
    (
      { routeName, isRole }: RoutesProps,
      account: ProfileAccountInfo = selectedAccount
    ) => {
      // const routeName = menu.routeName;
      // const isAdmin = account.isAdmin;
      const isAdminGroup = account.isAdminGroup;
      const isHidden = account.isHidden;
      const userPermission = account.userPermission;
      const accountLevel = account.accountLevel;
      const parentAccountLevel = account.parentLevel;

      let permission: boolean = true;

      if (!routeName) {
        return false;
      }

      if (isRole !== "rw") {
        permission = checkRoleMenu({
          permission,
          isRole,
          userPermission,
          account,
        });
      }

      if (
        routeName.includes("/manager") &&
        (routeName === "/manager/sitenotices" ||
          routeName === "/manager/emails") &&
        matchMedia(`screen and (max-width: ${mediaQuery.tabletMin}px)`).matches
      ) {
        return false;
      }
      if (routeName.startsWith("/help") || routeName.startsWith("/settings")) {
        if (routeName === "/help/techsupport") {
          return accountLevel === LEVEL_TYPE.CP;
        }
        if (routeName === "/help/contact") {
          return parentAccountLevel === LEVEL_TYPE.CP;
        }
        if (routeName === "/settings/adminAccount") {
          return isAdminGroup;
        }
        if (routeName === "/settings/information") {
          return account.accountLevel === LEVEL_TYPE.CP;
        }
        return true;
      }

      if (routeName === "/dashboard/map" || routeName === "/dashboard/chart") {
        if (account.accountLevel === LEVEL_TYPE.EU) {
          return account.isEnabled;
        }
        return accountLevel !== LEVEL_TYPE.DW;
      }

      if (routeName === "/dashboard/admin") {
        return accountLevel === LEVEL_TYPE.DW;
      }

      if (routeName.includes("/subscription")) {
        if (checkIsSubCP(selectedAccount) && routeName.includes("usage")) {
          return false;
        }

        if (
          accountLevel !== LEVEL_TYPE.DW &&
          (routeName.includes("/subscription/services") ||
            routeName.includes("/subscription/lyve"))
        ) {
          return false;
        }
        if (
          accountLevel !== LEVEL_TYPE.CP &&
          routeName.includes("/subscription/request-history")
        ) {
          return false;
        }
        if (
          routeName.includes("/subscription/usage/payment/cancel") &&
          accountLevel === LEVEL_TYPE.CP
        ) {
          return true;
        }

        if (accountLevel === LEVEL_TYPE.EU && routeName.includes("/keys")) {
          return false;
        }
        return permission;
      }

      if (accountLevel === LEVEL_TYPE.EU) {
        if (
          routeName.includes("/reports") ||
          routeName.includes("/alert") ||
          routeName === "/users/groups" ||
          routeName === "/recorders/groups"
        ) {
          return account.isEnabled;
        }
        if (account.isEnabled && isRole === "viewRecordersAndGroups") {
          return true;
        }
      }

      if (routeName.includes("/videoShare")) {
        if (accountLevel !== LEVEL_TYPE.EU) {
          return false;
        }
        return isHidden ? false : permission;
      }

      if (routeName.includes("/alert") || routeName.includes("/reports")) {
        return accountLevel !== LEVEL_TYPE.DW;
      }

      return permission;
    },
    [selectedAccount]
  );
  const getPermittedPath = useCallback(
    ({
      pathname,
      account,
      startPage,
    }: {
      pathname: string;
      account: ProfileAccountInfo;
      startPage?: number;
    }): string =>
      resolvePermittedPath({
        pathname,
        account,
        startPage: startPage ?? auth.user.startPage,
        checkRolePermission,
        unleashFlags: {
          isFirmwareUpgradeEnable,
          isCovaConfigEnable,
          isStagingRecorderEnable,
          isPlanHide,
        },
      }),
    [
      checkRolePermission,
      auth.user.startPage,
      isFirmwareUpgradeEnable,
      isCovaConfigEnable,
      isStagingRecorderEnable,
      isPlanHide,
    ]
  );

  // account 변경 시 페이지 이동 공통 함수
  const navigateSwitchAccount = async (
    options: SwitchAccountNavigateOptions,
    isHidden: boolean
  ) => {
    const roleId = getSwitchAccountRoleId(isHidden);
    try {
      const selectAccount = await getSwitchAccountInfo(
        options.accountId,
        roleId
      );
      if (!selectAccount || selectAccount.userPermission === undefined) {
        options.onFail?.();
        notifyError(lang.validateMsg.account.permissionsDeny);
        return;
      }

      if (checkIsSuspended(selectAccount) === SUSPENSION_STATUS.DISABLED) {
        options.onFail?.();
        auth.logout();
        notifyError(lang.ServerErrorCode["3019"]);
        return;
      }

      const queryString = mergeSwitchAccountQueryString(
        options.queryString,
        roleId
      );

      const isEnabled =
        checkIsSuspended(selectAccount) === SUSPENSION_STATUS.ENABLED;

      if (
        shouldRequire2faForSwitchAccount({
          isVerified: options.isVerified,
          userVerify2fa: auth.user.verify2fa,
          accountUse2faAuth: selectAccount.use2faAuth,
          userPermission: selectAccount.userPermission,
        })
      ) {
        const verify2fa = buildVerify2faNavigatePayload({
          selectAccount,
          verifyType: auth.user.use2faCode,
          email: auth.user.email,
          phone: auth.user.phone,
          userKey: auth.user.userKey,
        });
        options.onSuccess?.();
        navigate(verify2fa.pathname, {
          state: verify2fa.state,
        });
      } else {
        const account = changeToAccountFormat(selectAccount);
        const resolvedPath = resolveSwitchAccountNavigatePath({
          pathname: options.pathname,
          accountLevel: selectAccount.accountLevel,
          isEnabled,
        });
        const to = getPermittedPath({
          pathname: resolvedPath,
          account,
        });

        debouncedNavigate({
          to,
          state: { selectedAccount: selectAccount },
          queryString,
          replace: options.replace,
          onComplete: () => {
            options.onSuccess?.();
          },
        });
      }
    } catch (error: any) {
      handleErrorNotify(error, lang.label.org.notAvailable);
      return options.onFail?.();
    }
  };

  const checkPermissionLocation = async () => {
    const checkGeneration = ++permissionCheckGenerationRef.current;
    const redirectGuard = permissionRedirectGuardRef.current;

    //함수 실행될때 시작점 저장
    const pathnameRef = locationRef.current.pathname;
    const searchRef = locationRef.current.search;
    const selectedAccountIdRef = selectedAccountRef.current.accountId;
    const selectedRoleIdRef = selectedAccountRef.current.isHidden
      ? localStorage.getItem("r-hid")
      : localStorage.getItem("r-mem");

    //값이 변경됐으면 지금 함수 멈추는 함수
    //값이 변경됐거나 더 최신 검사가 시작됐으면 지금 함수 멈추는 함수
    const isStale = () =>
      checkGeneration !== permissionCheckGenerationRef.current ||
      locationRef.current.pathname !== pathnameRef ||
      locationRef.current.search !== searchRef ||
      selectedAccountRef.current.accountId !== selectedAccountIdRef;
    const token = getCookie(ACCESS_TOKEN);
    if (!isAuthenticated) {
      return;
    }
    if (!token) {
      console.log("checkPermissionLocation", "no token");
      dispatch(setIsAuth(false));
      window.dataLayer?.push({
        event: "expired",
        reason: "checkPermissionLocation no_token",
        page: window.location.pathname,
      });
      return;
    }
    const newSearchParams = new URLSearchParams(searchRef);
    const accountUrl = newSearchParams.get("account") as string | null;
    const targetAccountId = accountUrl || selectedAccountIdRef;
    const targetRoleId =
      (newSearchParams.get("r") as string) || selectedRoleIdRef;
    if (!targetAccountId || !targetRoleId) {
      const { pathname, search } = locationRef.current;
      if (search) {
        window.location.replace(pathname);
        return;
      }
      const recoveryKey = "mydw:permLocRecovery";
      if (sessionStorage.getItem(recoveryKey)) {
        sessionStorage.removeItem(recoveryKey);
        auth.logout();
        return;
      }
      sessionStorage.setItem(recoveryKey, "1");
      window.location.reload();
      return;
    }
    sessionStorage.removeItem("mydw:permLocRecovery");
    let account: ProfileAccountInfo = { ...selectedAccountRef.current };
    let requestAccount: ProfileAccountInfoFromServer | null = null;
    const isSelectedAccountTarget =
      targetAccountId === selectedAccountIdRef &&
      targetRoleId === selectedRoleIdRef;

    if (!isSelectedAccountTarget) {
      try {
        if (
          locationRef.current.state?.selectedAccount &&
          locationRef.current.state?.selectedAccount.accountId ===
            targetAccountId &&
          Array.isArray(
            locationRef.current.state?.selectedAccount.userPermission
          )
        ) {
          requestAccount = locationRef.current.state?.selectedAccount;
        } else {
          requestAccount = await getSwitchAccountInfo(
            targetAccountId,
            targetRoleId
          );
        }
        if (isStale()) {
          return;
        }
        if (!requestAccount) {
          const useKeyType = await getUseRoleType(
            targetAccountId,
            auth.user.email
          );
          if (!useKeyType) {
            return false;
          }
          menuNavigation({
            to: getPermittedPath({
              pathname: pathnameRef,
              account,
            }),
            queryString: {
              account: targetAccountId,
              r:
                useKeyType === "member"
                  ? (localStorage.getItem("r-mem") as string)
                  : (localStorage.getItem("r-hid") as string),
            },
            replace: true,
          });
          return;
        }

        account = changeToAccountFormat(requestAccount);
      } catch (error) {
        if (isStale()) {
          return;
        }
        return false;
      }
    }

    if (isStale()) {
      return;
    }

    const suspend = account.isSuspended;

    if (suspend === SUSPENSION_STATUS.DISABLED) {
      if (isStale()) {
        return;
      }
      auth.logout();
      return;
    }

    const unleashFlags = {
      isFirmwareUpgradeEnable,
      isCovaConfigEnable,
      isStagingRecorderEnable,
      isPlanHide,
    };
    const hasPermission = hasPermissionForPath({
      pathname: pathnameRef,
      account,
      checkRolePermission,
      unleashFlags,
    });

    if (!hasPermission) {
      if (isStale()) {
        return;
      }

      let to = getPermittedPath({
        pathname: pathnameRef,
        account,
      });
      if (normalizeMenuPath(to) === normalizeMenuPath(pathnameRef)) {
        to = getPermittedPath({
          pathname: getStartPageUrl(
            adjustedStartPage(account, auth.user.startPage)
          ),
          account,
        });
      }

      const fromKey = buildPermissionLocationKey(
        pathnameRef,
        account.accountId,
        targetRoleId
      );
      let toKey = buildPermissionLocationKey(
        to,
        account.accountId,
        targetRoleId
      );

      if (redirectGuard.wouldLoop(fromKey, toKey)) {
        to =
          account.accountLevel === LEVEL_TYPE.EU
            ? "/recorder/list"
            : "/account";
        toKey = buildPermissionLocationKey(to, account.accountId, targetRoleId);
        if (redirectGuard.wouldLoop(fromKey, toKey)) {
          return;
        }
      }

      if (isStale()) {
        return;
      }

      redirectGuard.record(fromKey, toKey);

      if (
        (targetAccountId !== selectedAccountRef.current.accountId ||
          targetRoleId !== selectedRoleIdRef) &&
        requestAccount
      ) {
        dispatch(setAccountSetting(requestAccount));
      }
      menuNavigation({
        to,
        queryString: {
          account: account.accountId,
          r: targetRoleId,
        },
        replace: true,
      });
      return false;
    }

    redirectGuard.reset();

    if (isStale()) {
      return;
    }

    setAxiosHeaders({
      roleId: targetRoleId,
    });

    if (
      (targetAccountId !== selectedAccountRef.current.accountId ||
        targetRoleId !== selectedRoleIdRef) &&
      requestAccount
    ) {
      dispatch(setAccountSetting(requestAccount));
    }
    return true;
  };

  const lists: RoutesProps[] = useMemo(
    () =>
      buildMenuListsForAccount({
        account: selectedAccount,
        checkRolePermission,
        unleashFlags: {
          isFirmwareUpgradeEnable,
          isCovaConfigEnable,
          isStagingRecorderEnable,
          isPlanHide,
        },
      }),
    [
      selectedAccount,
      checkRolePermission,
      isFirmwareUpgradeEnable,
      isCovaConfigEnable,
      isStagingRecorderEnable,
      isPlanHide,
    ]
  );

  const returnToStartPage = useCallback(
    ({
      startPage,
      account,
    }: {
      startPage?: number;
      account?: ProfileAccountInfo;
    } = {}) => {
      const userAccount = account ?? selectedAccount;
      const defaultPath = getStartPageUrl(
        adjustedStartPage(userAccount, startPage ?? auth.user.startPage)
      );
      const to = getPermittedPath({
        pathname: defaultPath,
        account: userAccount,
        startPage,
      });

      menuNavigation({
        to,
        replace: true,
      });
    },
    [auth.user.startPage, selectedAccount, getPermittedPath, menuNavigation]
  );

  const buildMenuRouteTo = useCallback(
    ({
      to,
      state,
      queryString,
      roleId,
    }: {
      to: string;
      replace?: boolean;
      state?: any;
      queryString?: Object;
      roleId?: string;
      onComplete?: () => void;
    }) => {
      let accountId: string = selectedAccount.accountId;
      if (state && state.selectedAccount) {
        accountId =
          typeof state.selectedAccount === "string"
            ? state.selectedAccount
            : state.selectedAccount.accountId;
      }

      const searchParams = new URLSearchParams();

      if (accountId) {
        searchParams.set("account", accountId);
      }

      if (queryString) {
        Object.entries(queryString).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            searchParams.set(key, String(value));
          }
        });
      }
      if (roleId && !searchParams.get("r")) {
        searchParams.set("r", roleId);
      }

      let rid = getRoleId({
        accountId: accountId,
      });
      if (rid && !searchParams.get("r")) {
        searchParams.set("r", rid);
      }
      return {
        pathname: to,
        search: searchParams.toString() ? `?${searchParams.toString()}` : "",
      };
    },
    [selectedAccount.accountId]
  );

  const buildEntryNavigateProps = useCallback(
    (to: string) => {
      return {
        to: buildMenuRouteTo({ to }),
        state: { state: { selectedAccount } },
      };
    },
    [buildMenuRouteTo, selectedAccount]
  );

  let currentMenu: RoutesProps | null = useMemo(() => {
    return (
      lists.find((item) =>
        location.pathname.startsWith(item.includeRouteName || "")
      ) || null
    );
  }, [lists, location.pathname]);
  return {
    lists,
    currentMenu,
    menuNavigation,
    checkRolePermission,
    queryNavigate,
    returnToStartPage,
    navigateSwitchAccount,
    debouncedNavigate,
    buildEntryNavigateProps,
    checkPermissionLocation,
    getPermittedPath,
    updateCurrentAccount,
  };
}
